import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Stepper, StepperStep } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import type { ContentKitElementProps } from '../types';
import { ContentKitStepperContext } from './context';

/**
 * Renderer for stepper element.
 *
 * Delegates the rendering of the active step to the Step renderer.
 */
export function ContentKitStepper(
    props: ContentKitElementProps<api.ContentKitStepper>
): React.ReactElement {
    const { element, dispatchAction, renderElement } = props;
    const { activeStep: activeStepId, onStepChange, onComplete, children: steps } = element;
    const [isDispatching, wrapDispatching] = useLoadingState(false);

    const activeStepIdx = steps.findIndex((step) => step.id === activeStepId);
    assert(activeStepIdx !== -1, 'Active step not found for stepper');
    const activeStep = steps[activeStepIdx];

    const navigateToStep = useEventCallback((step: string) =>
        wrapDispatching(async () => {
            await dispatchAction({ ...onStepChange, step });
        })
    );

    const completeStepper = useEventCallback(() =>
        wrapDispatching(async () => {
            await dispatchAction(onComplete);
        })
    );

    // Memoize the context value of the parent stepper with previous/next navigation functions
    const ctxValue = React.useMemo(() => {
        const previousStep = steps[activeStepIdx - 1];
        const nextStep = steps[activeStepIdx + 1];
        const isLastStep = activeStepIdx === steps.length - 1;

        /** The `previous` function is only available if the current step is not the first step */
        const previous = previousStep ? () => navigateToStep(previousStep.id) : undefined;

        /** If the current step is the last step, the next button should complete the stepper */
        const next = isLastStep ? completeStepper : () => navigateToStep(nextStep.id);

        return {
            previous,
            next,
            loading: isDispatching,
        };
    }, [steps, activeStepIdx, navigateToStep, completeStepper, isDispatching]);

    return (
        <ContentKitStepperContext.Provider value={ctxValue}>
            <div className="flex flex-col gap-6">
                {steps.length > 1 ? (
                    <Stepper value={activeStep.id} onChange={navigateToStep}>
                        {steps.map((step, idx) => (
                            <StepperStep key={step.id} id={step.id} title={step.title ?? idx} />
                        ))}
                    </Stepper>
                ) : null}
                {renderElement(activeStep)}
            </div>
        </ContentKitStepperContext.Provider>
    );
}
